/**
 * Minecarts (vanilla AbstractMinecart / Minecart): a cart snaps to the rail's centre line and follows
 * straights, corners and slopes; slopes pull it with 0.0078125 per tick, rails brake it (0.96 empty,
 * 0.997 ridden per tick), a powered rail pushes it along (+0.06 per tick, or a 0.02 launch from rest against
 * a solid block) and it moves at most 0.4 blocks per tick per axis (0.75x while ridden). Off the rails it is a
 * plain falling box: gravity 0.04, ground friction 0.5, air 0.95. Units are blocks and ticks (20 per second).
 *
 * A powered rail boosts only while redstone switches it on (world/redstone); unpowered it brakes.
 *
 * DOM-free: `MinecartCtx` is the world (block getter + collision shapes). `Minecart` is a `Rideable`
 * (game/riding.ts), so the player mounts it through the shared model. Rendering is render/minecart.ts and the
 * wiring (place, mount, punch, save) is entities/vehicles/minecartGlue.ts.
 */

import { collisionOf, IS_OPAQUE, RAIL_KIND, RAIL_SHAPE, RenderShape, RENDER_SHAPE } from '../../blocks';
import type { Rideable, RiderInput } from '../../game/riding';
import { clipMove } from '../../world/collide';
import { EXITS, isAscending, railAt, SHAPE_ASC_E, SHAPE_ASC_N, SHAPE_ASC_S, SHAPE_ASC_W, SHAPE_EW, SHAPE_NS } from '../../world/rails';

export interface MinecartCtx {
  getBlock(x: number, y: number, z: number): number;
}

export const CART_W = 0.98, CART_H = 0.7;
/** Max blocks a cart covers per tick along one axis (vanilla getMaxSpeed). */
export const MAX_SPEED = 0.4;
export const SLOPE_ACCEL = 0.0078125;
export const POWER_BOOST = 0.06;
const GRAVITY = 0.04;
/** Hits before it breaks: vanilla adds 10 per point of damage to a counter that breaks above 40. */
export const BREAK_DAMAGE = 40;
const RAIL_Y = 0.0625;

/** Height of the cart's feet above a rail cell's floor at local (lx, lz) of the rail's shape. */
export function railHeight(shape: number, lx: number, lz: number): number {
  const c = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
  const h = shape === SHAPE_ASC_E ? c(lx) : shape === SHAPE_ASC_W ? c(1 - lx) : shape === SHAPE_ASC_N ? c(1 - lz) : shape === SHAPE_ASC_S ? c(lz) : 0;
  return RAIL_Y + h;
}

let nextId = 1;

export class Minecart implements Rideable {
  readonly id: number;
  readonly kind = 'minecart';
  readonly seatCount = 1;
  readonly riderPose = 'sit' as const;
  readonly riderEyeOffset = -0.35;
  riders: (unknown | null)[] = [null];
  /** Feet, centred (the box is CART_W x CART_H above it). */
  x = 0; y = 0; z = 0;
  px = 0; py = 0; pz = 0;
  /** Velocity in blocks per tick. */
  vx = 0; vy = 0; vz = 0;
  /** Heading (atan2(dz, dx)), pitch (nose up) and the previous tick's, for render interpolation. */
  yaw = 0; pitch = 0; pyaw = 0; ppitch = 0;
  onGround = false;
  onRail = false;
  /** Shape of the rail it is on when sloped (-1 otherwise); the pitch follows the heading along it. */
  private slopeShape = -1;
  removed = false;
  damage = 0;
  hurtTime = 0;
  /** Dimension it lives in (carts only tick where the player is). */
  dim = 'overworld';
  /** Last rider input (Rideable.steer) and the horizontal push the rider's walking adds (set by the glue). */
  input: RiderInput = { forward: 0, strafe: 0, jump: false, sneak: false };
  pushX = 0; pushZ = 0;

  constructor(x: number, y: number, z: number, yaw = 0) {
    this.id = 0x4d430000 + nextId++;
    this.x = this.px = x; this.y = this.py = y; this.z = this.pz = z;
    this.yaw = this.pyaw = yaw;
  }

  steer(input: RiderInput): void { this.input = input; }
  get occupied(): boolean { return this.riders[0] != null; }
  /** Horizontal speed in blocks per tick. */
  get speed(): number { return Math.hypot(this.vx, this.vz); }

  seatPos(_i: number, out: { x: number; y: number; z: number }): void {
    out.x = this.x; out.y = this.y + 0.1; out.z = this.z;
  }
  /** A free standing spot beside the track (vanilla looks to the sides of the cart), else on top of it. */
  dismountPos(out: { x: number; y: number; z: number }, ctx?: MinecartCtx): void {
    out.x = this.x; out.y = this.y + 0.1; out.z = this.z;
    if (!ctx) return;
    const sx = -Math.sin(this.yaw), sz = Math.cos(this.yaw); // sideways of the heading
    for (const side of [1, -1]) {
      for (const dist of [1.2, 1.7]) {
        const x = this.x + sx * dist * side, z = this.z + sz * dist * side;
        const bx = Math.floor(x), bz = Math.floor(z);
        for (const dy of [0, 1, -1]) {
          const by = Math.floor(this.y) + dy;
          if (collisionOf(ctx.getBlock(bx, by - 1, bz)).length > 0 && collisionOf(ctx.getBlock(bx, by, bz)).length === 0
            && collisionOf(ctx.getBlock(bx, by + 1, bz)).length === 0) {
            out.x = x; out.y = by; out.z = z; return;
          }
        }
      }
    }
  }

  /** A hit. True when it broke (the caller drops the item and dismounts the rider). Creative breaks at once. */
  hurt(amount: number, creative: boolean): boolean {
    if (this.removed) return false;
    this.hurtTime = 10;
    this.damage += amount * 10;
    if (creative || this.damage > BREAK_DAMAGE) { this.removed = true; return true; }
    return false;
  }

  /** Ray (origin o, direction d, unit) against the cart's box: distance or -1. */
  hitDist(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, reach: number): number {
    const h = CART_W / 2;
    const lo = [this.x - h, this.y, this.z - h], hi = [this.x + h, this.y + CART_H, this.z + h];
    const o = [ox, oy, oz], d = [dx, dy, dz];
    let t0 = 0, t1 = reach;
    for (let a = 0; a < 3; a++) {
      if (Math.abs(d[a]) < 1e-9) { if (o[a] < lo[a] || o[a] > hi[a]) return -1; continue; }
      let a0 = (lo[a] - o[a]) / d[a], a1 = (hi[a] - o[a]) / d[a];
      if (a0 > a1) { const t = a0; a0 = a1; a1 = t; }
      if (a0 > t0) t0 = a0;
      if (a1 < t1) t1 = a1;
      if (t0 > t1) return -1;
    }
    return t0;
  }

  /** Box sweep against block shapes; returns the move actually made and sets onGround. */
  private move(ctx: MinecartCtx, dx: number, dy: number, dz: number): void {
    const shapes = (bx: number, by: number, bz: number): Float32Array => collisionOf(ctx.getBlock(bx, by, bz));
    const h = CART_W / 2;
    const box = [this.x - h, this.y, this.z - h, this.x + h, this.y + CART_H, this.z + h];
    const shift = (axis: number, d: number): void => { box[axis] += d; box[axis + 3] += d; };
    const ody = dy;
    dy = clipMove(box, 1, dy, 1e-7, shapes); shift(1, dy);
    dx = clipMove(box, 0, dx, 1e-7, shapes); shift(0, dx);
    dz = clipMove(box, 2, dz, 1e-7, shapes); shift(2, dz);
    this.x = (box[0] + box[3]) / 2; this.y = box[1]; this.z = (box[2] + box[5]) / 2;
    this.onGround = ody < 0 && dy !== ody;
    if (dy !== ody) this.vy = 0;
  }

  /** One game tick. */
  tick(ctx: MinecartCtx): void {
    this.px = this.x; this.py = this.y; this.pz = this.z;
    this.pyaw = this.yaw; this.ppitch = this.pitch;
    if (this.hurtTime > 0) this.hurtTime--;
    if (this.damage > 0) this.damage = Math.max(0, this.damage - 1);
    if (this.y < -64) { this.removed = true; return; }
    this.vy -= GRAVITY;

    const ix = Math.floor(this.x), iy = Math.floor(this.y), iz = Math.floor(this.z);
    // vanilla looks at the cell below first, then the cell itself
    let rail: [number, number, number, number] | null = null;
    const below = ctx.getBlock(ix, iy - 1, iz);
    if (RAIL_SHAPE[below] >= 0) rail = [ix, iy - 1, iz, below];
    else rail = railAt(ctx.getBlock.bind(ctx), ix, iy, iz);
    this.onRail = rail !== null;
    if (rail) this.alongTrack(ctx, rail);
    else this.offTrack(ctx);
    // face the way it rolls; tilt with the slope it is on
    if (this.speed > 0.01) this.yaw = Math.atan2(this.vz, this.vx);
    this.pitch = this.slopeShape >= 0 ? slopePitch(this.slopeShape, Math.cos(this.yaw), Math.sin(this.yaw)) : 0;
  }

  private alongTrack(ctx: MinecartCtx, [rx, ry, rz, id]: [number, number, number, number]): void {
    this.onGround = true;
    this.vy = 0;
    const shape = RAIL_SHAPE[id];
    const powered = RAIL_KIND[id] === 2; // switched on by redstone (world/redstone); kind 1 is the unpowered rail
    const oldY = ry + railHeight(shape, this.x - rx, this.z - rz);
    if (shape === SHAPE_ASC_E) this.vx -= SLOPE_ACCEL;
    else if (shape === SHAPE_ASC_W) this.vx += SLOPE_ACCEL;
    else if (shape === SHAPE_ASC_N) this.vz += SLOPE_ACCEL;
    else if (shape === SHAPE_ASC_S) this.vz -= SLOPE_ACCEL;

    const [a, b] = EXITS[shape];
    let ddx = b[0] - a[0], ddz = b[2] - a[2];
    const len = Math.hypot(ddx, ddz);
    if (this.vx * ddx + this.vz * ddz < 0) { ddx = -ddx; ddz = -ddz; }
    const spd = Math.min(2, this.speed);
    this.vx = (spd * ddx) / len; this.vz = (spd * ddz) / len;

    // the rider's own walking nudges a slow cart along the track
    if (this.occupied && (this.pushX !== 0 || this.pushZ !== 0) && this.vx * this.vx + this.vz * this.vz < 0.01) {
      const proj = ((this.vx + this.pushX * 0.1) * ddx + (this.vz + this.pushZ * 0.1) * ddz) / len;
      this.vx = (proj * ddx) / len; this.vz = (proj * ddz) / len;
    }

    // snap onto the rail's centre line
    const d0x = rx + 0.5 + a[0] * 0.5, d0z = rz + 0.5 + a[2] * 0.5;
    const lx = rx + 0.5 + b[0] * 0.5 - d0x, lz = rz + 0.5 + b[2] * 0.5 - d0z;
    let t: number;
    if (lx === 0) t = this.z - rz;
    else if (lz === 0) t = this.x - rx;
    else t = ((this.x - d0x) * lx + (this.z - d0z) * lz) * 2;
    this.x = d0x + lx * t; this.z = d0z + lz * t;
    this.y = ry + railHeight(shape, this.x - rx, this.z - rz);

    // roll along it
    const mult = this.occupied ? 0.75 : 1;
    const mx = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, mult * this.vx));
    const mz = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, mult * this.vz));
    const before = { x: this.x, z: this.z };
    this.y = ry + (isAscending(shape) ? 1 : 0); // vanilla moves a cart on a slope at its top height, so the block its high end rests against is not in the way
    this.move(ctx, mx, 0, mz);
    this.onGround = true;
    // walls on the track stop it (the move was clipped)
    if (Math.abs(this.x - before.x - mx) > 1e-6) this.vx = 0;
    if (Math.abs(this.z - before.z - mz) > 1e-6) this.vz = 0;

    // which rail is it over now? crossing into an exit cell continues on that cell's rail
    const nx = Math.floor(this.x), nz = Math.floor(this.z);
    let cx = rx, cy = ry, cz = rz, cshape = shape;
    if (nx !== rx || nz !== rz) {
      let found: [number, number, number, number] | null = null;
      for (const e of [a, b]) {
        if (rx + e[0] === nx && rz + e[2] === nz) found = railAtExit(ctx, nx, ry + e[1], nz);
      }
      found ??= railAtExit(ctx, nx, ry, nz);
      if (found) { [cx, cy, cz] = found; cshape = RAIL_SHAPE[found[3]]; }
    }
    const inCell = cx === Math.floor(this.x) && cz === Math.floor(this.z);
    this.y = inCell ? cy + railHeight(cshape, this.x - cx, this.z - cz) : oldY;
    this.slopeShape = inCell ? cshape : -1;

    // friction, then gravity's share of the height change
    const fr = this.occupied ? 0.997 : 0.96;
    this.vx *= fr; this.vz *= fr;
    if (inCell) {
      const ds = (oldY - this.y) * 0.05;
      const hs = this.speed;
      if (hs > 0) { const k = (hs + ds) / hs; this.vx *= k; this.vz *= k; }
    }
    // moved into the next cell: carry on along the axis it crossed on
    if (nx !== rx || nz !== rz) {
      const s = this.speed;
      this.vx = s * (nx - rx); this.vz = s * (nz - rz);
    }

    if (powered) {
      const s = this.speed;
      if (s > 0.01) {
        this.vx += (this.vx / s) * POWER_BOOST; this.vz += (this.vz / s) * POWER_BOOST;
      } else if (shape === SHAPE_EW || shape === SHAPE_NS) {
        // from rest against a solid block it is shoved away from it (vanilla isRedstoneConductor)
        const wall = (x: number, y: number, z: number): boolean => IS_OPAQUE[ctx.getBlock(x, y, z)] === 1 && RENDER_SHAPE[ctx.getBlock(x, y, z)] === RenderShape.Cube;
        if (shape === SHAPE_EW) {
          if (wall(rx - 1, ry, rz)) this.vx = 0.02; else if (wall(rx + 1, ry, rz)) this.vx = -0.02;
        } else if (wall(rx, ry, rz - 1)) this.vz = 0.02; else if (wall(rx, ry, rz + 1)) this.vz = -0.02;
      }
    } else if (RAIL_KIND[id] === 1) {
      // an unpowered powered rail is a brake (vanilla: stops below 0.03, else halves the speed)
      if (this.speed < 0.03) { this.vx = 0; this.vz = 0; } else { this.vx *= 0.5; this.vz *= 0.5; }
    }
  }

  private offTrack(ctx: MinecartCtx): void {
    this.slopeShape = -1;
    this.vx = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, this.vx));
    this.vz = Math.max(-MAX_SPEED, Math.min(MAX_SPEED, this.vz));
    if (this.onGround) { this.vx *= 0.5; this.vy *= 0.5; this.vz *= 0.5; }
    this.move(ctx, this.vx, this.vy, this.vz);
    if (!this.onGround) { this.vx *= 0.95; this.vy *= 0.95; this.vz *= 0.95; }
    if (Math.abs(this.vx) < 1e-4) this.vx = 0;
    if (Math.abs(this.vz) < 1e-4) this.vz = 0;
  }
}

function railAtExit(ctx: MinecartCtx, x: number, y: number, z: number): [number, number, number, number] | null {
  const below = ctx.getBlock(x, y - 1, z);
  if (RAIL_SHAPE[below] >= 0) return [x, y - 1, z, below];
  const here = ctx.getBlock(x, y, z);
  return RAIL_SHAPE[here] >= 0 ? [x, y, z, here] : null;
}

/** Nose-up angle of a cart travelling (vx, vz) on a rail of this shape. */
export function slopePitch(shape: number, vx: number, vz: number): number {
  if (!isAscending(shape)) return 0;
  const up = shape === SHAPE_ASC_E ? vx : shape === SHAPE_ASC_W ? -vx : shape === SHAPE_ASC_N ? -vz : vz;
  const a = Math.atan(1); // 45 degrees
  return up >= 0 ? a : -a;
}

/** All carts of a world. */
export class MinecartSim {
  readonly carts: Minecart[] = [];

  spawn(x: number, y: number, z: number, yaw = 0, dim = 'overworld'): Minecart {
    const c = new Minecart(x, y, z, yaw);
    c.dim = dim;
    this.carts.push(c);
    return c;
  }

  /** Tick the carts of `dim`; broken ones are dropped from the list. */
  tick(ctx: MinecartCtx, dim = 'overworld'): void {
    for (const c of this.carts) if (!c.removed && c.dim === dim) c.tick(ctx);
    for (let i = this.carts.length - 1; i >= 0; i--) if (this.carts[i].removed) this.carts.splice(i, 1);
  }

  /** Nearest cart on the ray within `reach`. */
  raycast(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, reach: number, dim = 'overworld'): { cart: Minecart; dist: number } | null {
    let best: { cart: Minecart; dist: number } | null = null;
    for (const c of this.carts) {
      if (c.removed || c.dim !== dim) continue;
      const d = c.hitDist(ox, oy, oz, dx, dy, dz, reach);
      if (d >= 0 && (!best || d < best.dist)) best = { cart: c, dist: d };
    }
    return best;
  }

  serialize(): number[][] {
    return this.carts.filter((c) => !c.removed).map((c) => [c.x, c.y, c.z, c.vx, c.vz, c.yaw, c.dim === 'overworld' ? 0 : c.dim === 'nether' ? 1 : 2]);
  }
  load(data: unknown): void {
    this.carts.length = 0;
    if (!Array.isArray(data)) return;
    for (const r of data) {
      if (!Array.isArray(r) || r.length < 7 || !r.slice(0, 7).every((v) => typeof v === 'number' && Number.isFinite(v))) continue;
      const c = this.spawn(r[0], r[1], r[2], r[5], r[6] === 1 ? 'nether' : r[6] === 2 ? 'end' : 'overworld');
      c.vx = r[3]; c.vz = r[4];
    }
  }
}
